import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Users,
  Building2,
  Plus,
  Pencil,
  Trash2,
  Mail,
  Phone,
  ExternalLink,
  Loader2,
  Briefcase,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import { mattersApi, contactsApi, clientsApi } from "../../services/api";

/* ── Types ──────────────────────────────────────────────────────────── */

interface ClientLink {
  clientId: any;
  role: string;
  billingRole: string;
  portalAccess?: boolean;
  notes?: string;
}

interface ExternalCounselEntry {
  _id: string;
  contactId: any;
  firmName: string | null;
  lawyerName: string | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
  role: string;
  securePortalUrl: string | null;
  notes: string | null;
}

interface PartiesTabProps {
  matterId: string;
  matter: any;
  client: any; // primary client (populated)
  onMatterUpdate: (m: any) => void;
}

/* ── Constants ─────────────────────────────────────────────────────── */

const ROLE_LABELS: Record<string, string> = {
  primary: "Primary",
  joint: "Joint",
  spouse_partner: "Spouse/Partner",
  dependent_child: "Dependent Child",
  sponsor: "Sponsor",
  beneficiary: "Beneficiary",
  executor: "Executor",
  trustee: "Trustee",
  respondent: "Respondent",
  plaintiff: "Plaintiff",
  defendant: "Defendant",
  other: "Other",
};

const BILLING_ROLE_LABELS: Record<string, string> = {
  bill_to: "Bill To",
  third_party_payer: "Third Party Payer",
  no_billing: "No Billing",
};

const COUNSEL_ROLE_OPTIONS = [
  { value: "other_side", label: "Other Side" },
  { value: "opposing", label: "Opposing Counsel" },
  { value: "co_counsel", label: "Co-Counsel" },
  { value: "agent", label: "Agent" },
  { value: "mediator", label: "Mediator" },
  { value: "arbitrator", label: "Arbitrator" },
  { value: "other", label: "Other" },
];

const COUNSEL_ROLE_LABELS: Record<string, string> = Object.fromEntries(
  COUNSEL_ROLE_OPTIONS.map((o) => [o.value, o.label])
);

const getInitials = (name: string) =>
  name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

/* ── Component ─────────────────────────────────────────────────────── */

const PartiesTab: React.FC<PartiesTabProps> = ({
  matterId,
  matter,
  client,
  onMatterUpdate,
}) => {
  const navigate = useNavigate();

  // ── Client Link state ──
  const [showAddClient, setShowAddClient] = useState(false);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [newClientLink, setNewClientLink] = useState({
    clientId: "",
    role: "primary",
    billingRole: "bill_to",
  });
  const [savingClient, setSavingClient] = useState(false);

  // ── External Counsel state ──
  const [showCounselModal, setShowCounselModal] = useState(false);
  const [editingCounsel, setEditingCounsel] = useState<ExternalCounselEntry | null>(null);
  const [contactOptions, setContactOptions] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [counselForm, setCounselForm] = useState({
    contactId: null as string | null,
    firmName: "",
    lawyerName: "",
    email: "",
    phone: "",
    fax: "",
    role: "other_side",
    securePortalUrl: "",
    notes: "",
  });
  const [savingCounsel, setSavingCounsel] = useState(false);

  // ── Load clients for SearchableSelect ──
  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const res = await clientsApi.list({ limit: 200 });
      const list = res.data?.data || res.data || [];
      setClientOptions(
        list.map((c: any) => ({ value: c._id, label: c.displayName }))
      );
    } catch {
      setClientOptions([]);
    } finally {
      setClientsLoading(false);
    }
  }, []);

  // ── Load contacts for SearchableSelect ──
  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const res = await contactsApi.list({ limit: 200 });
      const list = res.data?.data || res.data || [];
      setContactOptions(
        list.map((c: any) => ({
          value: c._id,
          label: c.displayName || c.organization?.name || "Unnamed",
        }))
      );
    } catch {
      setContactOptions([]);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchContacts();
  }, [fetchClients, fetchContacts]);

  // ── Client link handlers ──
  const handleAddClientLink = async () => {
    if (!newClientLink.clientId) return;
    setSavingClient(true);
    try {
      const res = await mattersApi.addClientLink(matterId, newClientLink);
      onMatterUpdate(res.data);
      setShowAddClient(false);
      setNewClientLink({ clientId: "", role: "primary", billingRole: "bill_to" });
    } catch (err) {
      console.error("Failed to add client link:", err);
    } finally {
      setSavingClient(false);
    }
  };

  const handleRemoveClientLink = async (clientId: string) => {
    try {
      const res = await mattersApi.removeClientLink(matterId, clientId);
      onMatterUpdate(res.data);
    } catch (err) {
      console.error("Failed to remove client link:", err);
    }
  };

  // ── External counsel handlers ──
  const openAddCounsel = () => {
    setEditingCounsel(null);
    setCounselForm({
      contactId: null,
      firmName: "",
      lawyerName: "",
      email: "",
      phone: "",
      fax: "",
      role: "other_side",
      securePortalUrl: "",
      notes: "",
    });
    setShowCounselModal(true);
  };

  const openEditCounsel = (entry: ExternalCounselEntry) => {
    setEditingCounsel(entry);
    setCounselForm({
      contactId: entry.contactId?._id || entry.contactId || null,
      firmName: entry.firmName || "",
      lawyerName: entry.lawyerName || "",
      email: entry.email || "",
      phone: entry.phone || "",
      fax: entry.fax || "",
      role: entry.role || "other_side",
      securePortalUrl: entry.securePortalUrl || "",
      notes: entry.notes || "",
    });
    setShowCounselModal(true);
  };

  const handleSaveCounsel = async () => {
    setSavingCounsel(true);
    try {
      const payload = {
        ...counselForm,
        contactId: counselForm.contactId || null,
        firmName: counselForm.firmName || null,
        lawyerName: counselForm.lawyerName || null,
        email: counselForm.email || null,
        phone: counselForm.phone || null,
        fax: counselForm.fax || null,
        securePortalUrl: counselForm.securePortalUrl || null,
        notes: counselForm.notes || null,
      };

      if (editingCounsel) {
        await mattersApi.updateExternalCounsel(matterId, editingCounsel._id, payload);
      } else {
        await mattersApi.addExternalCounsel(matterId, payload);
      }

      // Refetch matter to get updated data with populations
      const res = await mattersApi.get(matterId);
      onMatterUpdate(res.data);
      setShowCounselModal(false);
    } catch (err) {
      console.error("Failed to save external counsel:", err);
    } finally {
      setSavingCounsel(false);
    }
  };

  const handleDeleteCounsel = async (counselId: string) => {
    try {
      await mattersApi.removeExternalCounsel(matterId, counselId);
      const res = await mattersApi.get(matterId);
      onMatterUpdate(res.data);
    } catch (err) {
      console.error("Failed to remove external counsel:", err);
    }
  };

  // When a contact is selected, pre-fill fields
  const handleContactSelect = (contactId: string | null) => {
    setCounselForm((prev) => ({ ...prev, contactId }));
    if (!contactId) return;

    const contact = contactOptions.find((c) => c.value === contactId);
    if (contact) {
      // Fetch full contact to get email/phone
      contactsApi.get(contactId).then((res) => {
        const c = res.data;
        setCounselForm((prev) => ({
          ...prev,
          lawyerName:
            prev.lawyerName ||
            (c.person ? `${c.person.firstName || ""} ${c.person.lastName || ""}`.trim() : "") ||
            c.displayName ||
            "",
          firmName: prev.firmName || c.organization?.name || "",
          email: prev.email || c.emails?.[0]?.email || "",
          phone: prev.phone || c.phones?.[0]?.number || "",
        }));
      });
    }
  };

  const clientLinks: ClientLink[] = matter?.clientLinks || [];
  const externalCounsel: ExternalCounselEntry[] = matter?.externalCounsel || [];

  return (
    <div className="space-y-6">
      {/* ─── Our Clients Section ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-800">Our Clients</h3>
          </div>
          <button
            onClick={() => setShowAddClient(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Client
          </button>
        </div>

        <div className="space-y-2">
          {/* Primary Client */}
          {client && (
            <div className="flex items-center justify-between p-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
              <div className="flex items-center gap-3">
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold text-xs"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  {getInitials(client.displayName)}
                </div>
                <div>
                  <button
                    onClick={() => navigate(`/clients/${client._id}`)}
                    className="text-sm font-medium text-slate-800 hover:text-emerald-700 transition-colors"
                  >
                    {client.displayName}
                  </button>
                  <p className="text-xs text-slate-500">Primary Client</p>
                </div>
              </div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                Primary
              </span>
            </div>
          )}

          {/* Client Links */}
          {clientLinks.map((link, i) => {
            const linkClient =
              typeof link.clientId === "object" ? link.clientId : null;
            return (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full flex items-center justify-center bg-slate-200 text-slate-600 font-bold text-xs">
                    {getInitials(linkClient?.displayName || "?")}
                  </div>
                  <div>
                    <button
                      onClick={() =>
                        linkClient &&
                        navigate(`/clients/${linkClient._id}`)
                      }
                      className="text-sm font-medium text-slate-800 hover:text-emerald-700 transition-colors"
                    >
                      {linkClient?.displayName || "Unknown Client"}
                    </button>
                    <p className="text-xs text-slate-500">
                      {BILLING_ROLE_LABELS[link.billingRole] || link.billingRole}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    {ROLE_LABELS[link.role] || link.role}
                  </span>
                  <button
                    onClick={() =>
                      linkClient &&
                      handleRemoveClientLink(String(linkClient._id))
                    }
                    className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          {!client && clientLinks.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">
              No clients linked to this matter
            </p>
          )}
        </div>
      </div>

      {/* ─── External Counsel Section ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-slate-800">External Counsel</h3>
          </div>
          <button
            onClick={openAddCounsel}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-50 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Counsel
          </button>
        </div>

        {externalCounsel.length > 0 ? (
          <div className="space-y-2">
            {externalCounsel.map((entry) => (
              <motion.div
                key={entry._id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-slate-50 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 font-bold text-xs mt-0.5">
                      {getInitials(entry.lawyerName || entry.firmName || "?")}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">
                        {entry.lawyerName || "Unknown Lawyer"}
                      </p>
                      {entry.firmName && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Building2 className="h-3 w-3 text-slate-400" />
                          <p className="text-xs text-slate-500">
                            {entry.firmName}
                          </p>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-1.5">
                        {entry.email && (
                          <a
                            href={`mailto:${entry.email}`}
                            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 transition-colors"
                          >
                            <Mail className="h-3 w-3" />
                            {entry.email}
                          </a>
                        )}
                        {entry.phone && (
                          <a
                            href={`tel:${entry.phone}`}
                            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 transition-colors"
                          >
                            <Phone className="h-3 w-3" />
                            {entry.phone}
                          </a>
                        )}
                        {entry.securePortalUrl && (
                          <a
                            href={entry.securePortalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Secure Portal
                          </a>
                        )}
                      </div>
                      {entry.notes && (
                        <p className="text-xs text-slate-400 mt-1">
                          {entry.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {COUNSEL_ROLE_LABELS[entry.role] || entry.role}
                    </span>
                    <button
                      onClick={() => openEditCounsel(entry)}
                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteCounsel(entry._id)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Briefcase className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              No external counsel added yet
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Add lawyers from the other side, co-counsel, mediators, etc.
            </p>
          </div>
        )}
      </div>

      {/* ─── Add Client Link Modal ─── */}
      <PremiumModal
        open={showAddClient}
        onClose={() => setShowAddClient(false)}
        title="Add Client"
        subtitle="Link an additional client to this matter"
        icon={User}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowAddClient(false)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddClientLink}
              disabled={!newClientLink.clientId || savingClient}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {savingClient ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Add Client"
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Client
            </label>
            <SearchableSelect
              options={clientOptions}
              value={newClientLink.clientId || null}
              onChange={(v) =>
                setNewClientLink((prev) => ({ ...prev, clientId: v || "" }))
              }
              placeholder="Select a client..."
              loading={clientsLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Role
            </label>
            <SearchableSelect
              options={Object.entries(ROLE_LABELS).map(([val, label]) => ({ value: val, label }))}
              value={newClientLink.role}
              onChange={(v) =>
                setNewClientLink((prev) => ({ ...prev, role: v || "primary" }))
              }
              placeholder="Select role..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Billing Role
            </label>
            <SearchableSelect
              options={Object.entries(BILLING_ROLE_LABELS).map(([val, label]) => ({ value: val, label }))}
              value={newClientLink.billingRole}
              onChange={(v) =>
                setNewClientLink((prev) => ({
                  ...prev,
                  billingRole: v || "bill_to",
                }))
              }
              placeholder="Select billing role..."
            />
          </div>
        </div>
      </PremiumModal>

      {/* ─── External Counsel Modal ─── */}
      <PremiumModal
        open={showCounselModal}
        onClose={() => setShowCounselModal(false)}
        title={editingCounsel ? "Edit External Counsel" : "Add External Counsel"}
        subtitle="Lawyer from the other side, co-counsel, mediator, etc."
        icon={Briefcase}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowCounselModal(false)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveCounsel}
              disabled={savingCounsel}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {savingCounsel ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : editingCounsel ? (
                "Save Changes"
              ) : (
                "Add Counsel"
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Link to Contact */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Link to Contact{" "}
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <SearchableSelect
              options={contactOptions}
              value={counselForm.contactId}
              onChange={handleContactSelect}
              placeholder="Search contacts to auto-fill..."
              loading={contactsLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Lawyer Name
              </label>
              <input
                type="text"
                value={counselForm.lawyerName}
                onChange={(e) =>
                  setCounselForm((prev) => ({
                    ...prev,
                    lawyerName: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="e.g. Jane Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Firm Name
              </label>
              <input
                type="text"
                value={counselForm.firmName}
                onChange={(e) =>
                  setCounselForm((prev) => ({
                    ...prev,
                    firmName: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="e.g. Smith & Associates"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={counselForm.email}
                onChange={(e) =>
                  setCounselForm((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="jane@smithlaw.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <input
                type="text"
                value={counselForm.phone}
                onChange={(e) =>
                  setCounselForm((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="(416) 555-1234"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Fax
              </label>
              <input
                type="text"
                value={counselForm.fax}
                onChange={(e) =>
                  setCounselForm((prev) => ({ ...prev, fax: e.target.value }))
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="(416) 555-5678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Role
              </label>
              <SearchableSelect
                options={COUNSEL_ROLE_OPTIONS}
                value={counselForm.role}
                onChange={(v) =>
                  setCounselForm((prev) => ({ ...prev, role: v || "other_side" }))
                }
                placeholder="Select role..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Secure Portal URL{" "}
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="url"
              value={counselForm.securePortalUrl}
              onChange={(e) =>
                setCounselForm((prev) => ({
                  ...prev,
                  securePortalUrl: e.target.value,
                }))
              }
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="https://portal.example.com/deal/123"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes
            </label>
            <textarea
              value={counselForm.notes}
              onChange={(e) =>
                setCounselForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              placeholder="Any additional notes..."
            />
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default PartiesTab;
